import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_performance-tracker-140/artifacts/swckjj4a_LD%20Logo.png";

function formatApiError(detail) {
  if (detail == null) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [registerName, setRegisterName] = useState("");

  const onGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onPasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Mirror cookie into localStorage so file/PDF download links (?auth=token) work
      localStorage.setItem("session_token", data.session_token);
      toast.success(`Welcome back, ${data.user.name}`);
      // Hard navigation to dashboard so AuthContext re-runs checkAuth with the now-set cookie+localStorage.
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    }
    setSubmitting(false);
  };

  const onSelfRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || password.length < 8) {
      toast.error("Email + password (min 8 chars) required"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/register", { email, password, name: registerName || email.split("@")[0] });
      toast.success("Account created — awaiting admin approval. You'll receive an email once approved.");
      setMode("login");
      setPassword("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Registration failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex bg-[#F4F1EA]">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="w-full max-w-sm animate-fade-up">
          {/* Brand — compact logo above the form */}
          <div className="flex flex-col items-center text-center mb-4 sm:mb-6">
            <img
              src={LOGO_URL}
              alt="Land Drafters"
              className="h-20 sm:h-24 lg:h-28 w-auto object-contain"
            />
            <div className="label-mono mt-1">Survey · Drafting · Engineering Ops</div>
            <div className="ld-brand-strip w-full max-w-xs mt-2" aria-hidden="true" />
          </div>

          <div className="brut-card p-5 sm:p-6">
            <div className="label-mono mb-2">SECURE ACCESS</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2 text-[#1B2A25]">
              Organize. Track. <span className="bg-[#1F5F3F] text-white px-2 inline-block">Improve.</span>
            </h1>
            <p className="text-xs sm:text-sm text-[#4A5952] mt-2 mb-4 leading-relaxed">
              Track every job, drafter handoff, QA stage, attendance, invoice and reminder — the official operations cockpit for Land Drafters.
            </p>

            <button
              onClick={onGoogleLogin}
              data-testid="google-login-btn"
              className="w-full brut-btn px-5 py-2.5 text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-3"
            >
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13 4.5 4 13.5 4 24.5s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.4 4.5 9.8 8.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 44.5c5.2 0 9.9-2 13.4-5.3l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.7 40.2 16.3 44.5 24 44.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.2C40.6 36.2 44 30.7 44 24.5c0-1.3-.1-2.7-.4-4z"/></svg>
              CONTINUE WITH GOOGLE
            </button>

            <div className="flex items-center gap-3 my-3 sm:my-4">
              <div className="flex-1 h-px bg-[#D6C9A3]" />
              <span className="text-[10px] font-mono text-[#8B6508] tracking-widest">OR EMAIL + PASSWORD</span>
              <div className="flex-1 h-px bg-[#D6C9A3]" />
            </div>

            <form onSubmit={mode === "register" ? onSelfRegister : onPasswordLogin} className="space-y-2.5" data-testid="password-login-form">
              {mode === "register" && (
                <input
                  type="text"
                  value={registerName}
                  onChange={(e) => setRegisterName(e.target.value)}
                  placeholder="Full name"
                  className="brut-input w-full px-3 py-2 text-sm font-mono"
                  data-testid="register-name-input"
                  autoComplete="name"
                />
              )}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourorg.com"
                className="brut-input w-full px-3 py-2 text-sm font-mono"
                data-testid="login-email-input"
                autoComplete="email"
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Choose a password (min 8 chars)" : "Password"}
                className="brut-input w-full px-3 py-2 text-sm font-mono"
                data-testid="login-password-input"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full brut-btn-secondary px-5 py-2 text-xs sm:text-sm uppercase tracking-wider disabled:opacity-60"
                data-testid="password-login-submit"
              >
                {submitting
                  ? (mode === "register" ? "REGISTERING…" : "SIGNING IN…")
                  : (mode === "register" ? "REQUEST ACCESS" : "SIGN IN")}
              </button>
              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setPassword(""); }}
                className="w-full text-[10px] sm:text-xs font-mono text-[#8B6508] hover:text-[#1F5F3F] underline pt-1"
                data-testid="login-toggle-register"
              >
                {mode === "login" ? "No account? Request access →" : "← Already have an account? Sign in"}
              </button>
            </form>

            <div className="mt-4 pt-3 border-t border-[#D6C9A3]">
              <div className="label-mono mb-1.5">PIPELINE STAGES</div>
              <div className="flex flex-wrap gap-1">
                {["ASSIGN","DRAFTER","QA1","QA2","FINAL QC","CLIENT"].map((s)=>(
                  <span key={s} className="text-[9px] font-mono font-bold px-1.5 py-0.5 bg-[#F5F1E8] border border-[#1B2A25] text-[#1B2A25]">{s}</span>
                ))}
              </div>
            </div>
          </div>

          <p className="text-[10px] sm:text-xs font-mono text-[#4A5952] text-center mt-3">
            New sign-ups (Google or email) need admin approval before login is allowed.
          </p>
        </div>
      </div>

      {/* Right brand panel — hidden on smaller / shorter screens to keep form 100% accessible */}
      <div className="hidden xl:flex flex-1 bg-[#1B2A25] relative overflow-hidden border-l border-[#1B2A25] items-center justify-center">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://static.prod-images.emergentagent.com/jobs/3d603652-8b12-4301-a330-4fdd7419771d/images/20fd73949a3e3853b32f1a874baecb7b840604ea1011c5426616abd695981c5a.png")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(100%) sepia(0.3) hue-rotate(65deg) brightness(0.9)",
          }}
        />
        <div className="relative z-10 max-w-xl text-center p-8">
          <img src={LOGO_URL} alt="Land Drafters" className="h-32 w-auto mx-auto mb-5 object-contain" />
          <div
            className="font-mono text-[10px] tracking-[0.28em] uppercase mb-3 font-bold"
            style={{ color: "#E5C46B" }}
          >
            // Control Room
          </div>
          <h2 className="font-heading text-2xl xl:text-3xl font-black tracking-tight leading-tight text-white">
            Drafter <span className="text-[#4CAF7C]">→</span> QA1 <span className="text-[#4CAF7C]">→</span> QA2 <span className="text-[#4CAF7C]">→</span> Final QC <span className="text-[#E5C46B]">→</span> <span className="bg-[#B8860B] text-[#1B2A25] px-2">Sent</span>
          </h2>
          <p
            className="text-sm mt-4 font-mono italic"
            style={{ color: "#F5E7B8" }}
          >
            &ldquo;Every Activity Matters.&rdquo;
          </p>
        </div>
      </div>
    </div>
  );
}
